/**
 * 팀 검색 관련 함수들
 */

import { createBrowserClient } from '@/supabase'
import { getTeamMemberAccountsBatch, TEAM_MEMBER_STATUS, logTeamService } from '@/utils/team-helpers'
import type { TeamSearchParams } from '../team-types'
import type { PaginationParams, PaginationResponse } from '@/types/common'
import type { Enums } from '@/types/supabase'

/**
 * 팀 검색 (기업 고객용)
 */
export const searchTeams = async (
  params: TeamSearchParams,
  pagination?: PaginationParams
): Promise<PaginationResponse<any>> => {
  const { searchTerm, specialty, subSpecialty, techStack, preferredProjectPeriod, preferredContractAmount } =
    params
  const { page = 1, pageSize = 10 } = pagination || {}
  const supabase = createBrowserClient()
  type PreferredProjectPeriod = Enums<'counsel_period'> | null
  type PreferredContractAmount = Enums<'counsel_cost'> | null

  // Supabase는 0부터 시작하므로 인덱스 계산
  const from = (page - 1) * pageSize
  const to = from + pageSize - 1

  // 기본 쿼리: 삭제되지 않은 팀만 조회
  let query = supabase
    .from('teams')
    .select(
      `
      id,
      name,
      bio,
      specialty,
      sub_specialty,
      prefered,
      preferred_project_period,
      preferred_contract_amount,
      created_at,
      manager_profile_id
    `,
      { count: 'exact' }
    )
    .is('deleted_at', null)

  // 선호 프로젝트 기간 필터 (서버 측 필터링 - 단순 값 비교)
  if (preferredProjectPeriod && preferredProjectPeriod.length > 0) {
    query = query.in('preferred_project_period', preferredProjectPeriod as PreferredProjectPeriod[])
  }

  // 선호 계약 금액 필터 (서버 측 필터링 - 단순 값 비교)
  if (preferredContractAmount && preferredContractAmount.length > 0) {
    query = query.in('preferred_contract_amount', preferredContractAmount as PreferredContractAmount[])
  }

  // 정렬 및 범위 설정
  const { data: teams, error, count } = await query
    .order('created_at', { ascending: false })
    .range(from, to)

  if (error) {
    logTeamService('error', 'searchTeams', error)
    throw error
  }

  if (!teams || teams.length === 0) {
    return { data: [], count: 0, nextPage: undefined }
  }

  interface TeamSearchRow {
    id: number
    name: string
    bio?: string | null
    specialty?: string[] | null
    sub_specialty?: string[] | null
    prefered?: string[] | null
    preferred_project_period?: string | null
    preferred_contract_amount?: string | null
    created_at?: string | null
    manager_profile_id: string | null
  }
  const teamsTyped = (teams ?? []) as unknown as TeamSearchRow[]

  // 클라이언트 측 필터링 (배열 포함 검사)
  let filteredTeams = teamsTyped

  // 전문분야 필터 (배열에 포함된 값이 있는지 확인)
  if (specialty && specialty.length > 0) {
    filteredTeams = filteredTeams.filter((team) => {
      if (!team.specialty || !Array.isArray(team.specialty)) return false
      return specialty.some((spec) => team.specialty?.includes(spec))
    })
  }

  // 세부 전문분야 필터
  if (subSpecialty && subSpecialty.length > 0) {
    filteredTeams = filteredTeams.filter((team) => {
      if (!team.sub_specialty || !Array.isArray(team.sub_specialty)) return false
      return subSpecialty.some((sub) => team.sub_specialty?.includes(sub))
    })
  }

  // 기술 스택 필터
  if (techStack && techStack.length > 0) {
    filteredTeams = filteredTeams.filter((team) => {
      if (!team.prefered || !Array.isArray(team.prefered)) return false
      return techStack.some((tech) => team.prefered?.includes(tech))
    })
  }

  // 검색어 필터 (팀 이름, 소개, 기술스택, 서비스 분야 통합 검색)
  if (searchTerm) {
    const searchLower = searchTerm.toLowerCase().trim()
    filteredTeams = filteredTeams.filter((team) => {
      if (team.name?.toLowerCase().includes(searchLower)) return true
      if (team.bio?.toLowerCase().includes(searchLower)) return true
      if (team.prefered && Array.isArray(team.prefered)) {
        if (team.prefered.some((tech: string) => tech?.toLowerCase().includes(searchLower))) return true
      }
      if (team.specialty && Array.isArray(team.specialty)) {
        if (team.specialty.some((spec: string) => spec?.toLowerCase().includes(searchLower))) return true
      }
      if (team.sub_specialty && Array.isArray(team.sub_specialty)) {
        if (team.sub_specialty.some((sub: string) => sub?.toLowerCase().includes(searchLower))) return true
      }
      return false
    })
  }

  if (filteredTeams.length === 0) {
    return { data: [], count: 0, nextPage: undefined }
  }

  // 모든 팀 ID 수집
  const teamIds = filteredTeams
    .map((t) => t.id)
    .filter((id): id is number => id !== null && id !== undefined)

  // 모든 팀의 팀원 정보를 한 번에 조회 (성능 최적화)
  const { data: allTeamMembers } = await supabase
    .from('team_members')
    .select('team_id, profile_id')
    .in('team_id', teamIds)
    .eq('status', TEAM_MEMBER_STATUS.ACTIVE)

  interface TeamMemberSearchRow {
    team_id: number | null
    profile_id: string | null
  }
  const allTeamMembersTyped = (allTeamMembers || []) as unknown as TeamMemberSearchRow[]

  // 모든 매니저와 팀원의 profile_id 수집
  const allManagerProfileIds = filteredTeams.map((t) => t.manager_profile_id).filter(Boolean) as string[]

  const allMemberProfileIds = allTeamMembersTyped
    .map((m) => m.profile_id)
    .filter(Boolean) as string[]

  const allProfileIds = Array.from(new Set([...allManagerProfileIds, ...allMemberProfileIds]))

  // 모든 계정 정보를 한 번에 조회 (성능 최적화)
  const accountsMap = await getTeamMemberAccountsBatch(supabase as any, allProfileIds)

  // 팀별 팀원 그룹화
  const membersByTeamId = new Map<number, TeamMemberSearchRow[]>()
  for (const member of allTeamMembersTyped) {
    const teamId = member.team_id
    if (teamId && !membersByTeamId.has(teamId)) {
      membersByTeamId.set(teamId, [])
    }
    if (teamId) {
      membersByTeamId.get(teamId)!.push(member)
    }
  }

  // 각 팀에 매니저와 팀원 정보 매핑
  const enrichedTeams = filteredTeams
    .map((team) => {
      if (!team.id) return null
      const teamMembers = membersByTeamId.get(team.id) || []
      
      // active 멤버가 없는 팀은 제외
      if (teamMembers.length === 0) {
        return null
      }
      
      const memberProfileIds = teamMembers.map((m) => m.profile_id).filter(Boolean) as string[]

      const enrichedMembers = memberProfileIds.map((profileId) => {
        const account = accountsMap.get(profileId)
        return {
          account: account ? { username: account.username } : null,
        }
      })

      const managerAccount = team.manager_profile_id ? accountsMap.get(team.manager_profile_id) : null

      return {
        ...team,
        manager: managerAccount ? { username: managerAccount.username } : null,
        team_members: enrichedMembers,
      }
    })
    .filter((team): team is NonNullable<typeof team> => team !== null)

  // 다음 페이지가 있는지 확인
  const hasNextPage = enrichedTeams.length === pageSize && (count ?? 0) > page * pageSize

  return {
    data: enrichedTeams,
    count,
    nextPage: hasNextPage ? page + 1 : undefined,
  }
}

